import { ReloadOutlined } from '@ant-design/icons'
import type { Home } from '@lmc/ts-common'
import { Button, CheckboxGroup } from '@lmc/ui-kit'
import { Drawer } from 'antd'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { reportsConstants } from 'locale/team'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type { StatusFilter, TeamFilters } from '../../utils/teamFilters'

type TeamFiltersPanelProps = {
  open: boolean
  onClose: () => void
  // Currently-applied filters (the source of truth lives in the screen).
  filters: TeamFilters
  onApply: (filters: TeamFilters) => void
  // Site (home) options, sourced from the screen's resolved homes list.
  homes: Home[]
  testId?: string
}

type CheckboxValueType = string | number | boolean

// Mirror the Edit-permissions side panel placement (CM-3581).
const PANEL_WIDTH = 411
const DIVIDER = '#f0f0f0'

const StyledDrawer = styled(Drawer)`
  .ant-drawer-header-title {
    flex-direction: row-reverse;
  }

  /* Full-width divider lines from the design (#f0f0f0): under the header, above
     the footer, and between the body sections (see Section below). */
  .ant-drawer-header {
    border-bottom: 1px solid ${DIVIDER};
  }

  .ant-drawer-title {
    font-weight: 500;
    font-size: 24px;
    line-height: 32px;
    color: ${({ theme }) => theme.color.neutral.color2};
  }

  /* No body padding — each section owns its padding so the dividers span the
     full panel width, matching the header/footer lines. */
  .ant-drawer-body {
    padding: 0;
  }

  .ant-drawer-footer {
    border-top: 1px solid ${DIVIDER};
    padding: 16px;
  }
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
`

const ResetRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 4px 16px 0;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;

  /* Divider between sections (Status / Site); the last section sits flush above
     the footer's own top border. */
  &:not(:last-child) {
    border-bottom: 1px solid ${DIVIDER};
  }
`

const SectionTitle = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const StyledCheckboxGroup = styled(CheckboxGroup)`
  display: flex !important;
  flex-direction: column !important;
  gap: 8px;

  .ant-checkbox-wrapper {
    margin-left: 0;
  }
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
`

// Group-dashboard Filters surface: a right-hand side panel mirroring the
// Edit-permissions panel — header ("Applying filters"), a top-right "Reset
// filters" link, Status + Site sections (divided by a line), and a Cancel /
// Apply footer. Site comes from the group's homes list; role is excluded (it's
// the tab row). Draft-then-apply: Apply commits, Cancel/close discards (CM-3581).
const TeamFiltersPanel: React.FC<TeamFiltersPanelProps> = ({
  open,
  onClose,
  filters,
  onApply,
  homes,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.TEAM)
  const { t: tGeneral } = useTranslation(NAMESPACE.GENERAL)
  const [draftStatuses, setDraftStatuses] = useState<StatusFilter[]>(
    filters.statuses
  )
  const [draftSiteIds, setDraftSiteIds] = useState<string[]>(filters.siteIds)

  // Re-seed the draft from the applied filters each time the panel opens, so a
  // cancelled edit never lingers into the next open.
  useEffect(() => {
    if (open) {
      setDraftStatuses(filters.statuses)
      setDraftSiteIds(filters.siteIds)
    }
  }, [open, filters.statuses, filters.siteIds])

  const statusOptions = [
    { value: 'active', label: t(reportsConstants.ACTIVE) },
    { value: 'inactive', label: t(reportsConstants.INACTIVE) }
  ]

  // Guard against homes with a missing id (mirrors the Add-member wizard's Site
  // picker) and de-duplicate by id (mirrors getSiteNames), so the picker can't
  // render a "undefined"-valued or duplicate checkbox (CM-3581 review).
  const siteOptions = Array.from(
    new Map(
      homes
        .filter((home) => home?.id)
        .map((home) => [String(home.id), home.name])
    ),
    ([value, label]) => ({ value, label })
  )

  const handleApply = () => {
    onApply({ statuses: draftStatuses, siteIds: draftSiteIds })
    onClose()
  }

  // Cancel and drawer-dismiss (mask / Esc) discard the draft — the design's
  // "Cancel/close discards". Resetting here, not only via the re-seed effect on
  // the next open, stops a dismissed edit from flashing back when reopened.
  const handleClose = () => {
    setDraftStatuses(filters.statuses)
    setDraftSiteIds(filters.siteIds)
    onClose()
  }

  const handleReset = () => {
    setDraftStatuses([])
    setDraftSiteIds([])
  }

  return (
    <StyledDrawer
      title={t(reportsConstants.TEAM_LIST_FILTER_PANEL_TITLE)}
      placement="right"
      width={PANEL_WIDTH}
      open={open}
      onClose={handleClose}
      destroyOnClose
      footer={
        <Footer>
          <Button
            onClick={handleClose}
            data-testid={testId ? `${testId}-cancel` : undefined}
          >
            {tGeneral(generalConstants.CANCEL)}
          </Button>
          <Button
            variant="primary"
            onClick={handleApply}
            data-testid={testId ? `${testId}-apply` : undefined}
          >
            {t(reportsConstants.TEAM_LIST_FILTER_APPLY)}
          </Button>
        </Footer>
      }
    >
      <Body data-testid={testId}>
        <ResetRow>
          <Button
            variant="text"
            icon={<ReloadOutlined />}
            onClick={handleReset}
            data-testid={testId ? `${testId}-reset` : undefined}
          >
            {t(reportsConstants.TEAM_LIST_FILTER_RESET)}
          </Button>
        </ResetRow>
        <Section>
          <SectionTitle>
            {t(reportsConstants.TEAM_LIST_FILTER_STATUS)}
          </SectionTitle>
          <StyledCheckboxGroup
            aria-label={t(reportsConstants.TEAM_LIST_FILTER_STATUS)}
            options={statusOptions}
            value={draftStatuses}
            onChange={(values: CheckboxValueType[]) =>
              setDraftStatuses(values as StatusFilter[])
            }
            data-testid={testId ? `${testId}-status` : undefined}
          />
        </Section>
        <Section>
          <SectionTitle>
            {t(reportsConstants.TEAM_LIST_FILTER_SITE)}
          </SectionTitle>
          <StyledCheckboxGroup
            aria-label={t(reportsConstants.TEAM_LIST_FILTER_SITE)}
            options={siteOptions}
            value={draftSiteIds}
            onChange={(values: CheckboxValueType[]) =>
              setDraftSiteIds(values as string[])
            }
            data-testid={testId ? `${testId}-site` : undefined}
          />
        </Section>
      </Body>
    </StyledDrawer>
  )
}

export default TeamFiltersPanel
